import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { createSelector } from 'reselect'

import {
  getCurrentUser,
  getFreeTrialStatus,
  getUserIsAdmin,
} from '../../selectors/user.selectors'
import { TrialStatus } from '../../reducers/auth/userReducer'
import {
  selectPayrollCompany,
  selectPayrollProfile,
  selectPayrollState,
} from '../../features/Payroll/payroll.selectors'
import NavigationCountIcon from './NavigationCountIcon'
import { selectUserPermissions } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'

// TODO: Update as part of https://linear.app/heard/issue/ZEN-2064/remove-backwards-compatibility-for-priceproduct-id-checks
export const selectShouldShowPayrollSideNavLink = createSelector(
  selectPayrollState,
  selectUserPermissions,
  (_: unknown, permissionCheckEnabled: boolean) => permissionCheckEnabled,
  (payrollState, userPermissions, permissionCheckEnabled) =>
    Boolean(payrollState) &&
    (!permissionCheckEnabled ||
      userPermissions.includes(ProductFeatureId.payroll))
)
export const selectPayrollNavConfig = createSelector(
  selectShouldShowPayrollSideNavLink,
  selectPayrollProfile,
  selectPayrollCompany,
  getCurrentUser,
  getUserIsAdmin,
  getFreeTrialStatus,
  (
    shouldShowPayrollSideNavLink,
    payrollProfile,
    payrollCompany,
    currentUser,
    isAdmin,
    trialStatus
  ) => {
    if (shouldShowPayrollSideNavLink) {
      return {
        payroll: {
          icon: regular('envelope-open-dollar'),
          label: 'Payroll',
          to: '/payroll/welcome',
          end: true,
        },
      }
    }

    const isFreeTrial = trialStatus === TrialStatus.active

    // If the user is a trial user they should only be able to use Heard payroll if they have it enabled and are migrating
    const showPayrollLink =
      Boolean(currentUser?.payrollEnabledAt) &&
      (!isFreeTrial || payrollProfile?.needsMigration) &&
      !isAdmin

    if (!showPayrollLink) {
      return {} as Record<string, never>
    }

    if (
      !payrollProfile?.isActive ||
      payrollCompany?.company_status !== 'Approved'
    ) {
      return {
        payroll: {
          icon: regular('envelope-open-dollar'),
          label: 'Payroll',
          to: payrollProfile?.needsMigration
            ? '/payroll/migrate'
            : '/payroll/enroll/intro',
          ornament: <NavigationCountIcon count={1} />,
        },
      }
    }

    return {
      payroll: {
        icon: regular('envelope-open-dollar'),
        label: 'Payroll',
        pages: [
          { end: true, label: 'Run Payroll', to: '/payroll/run_payroll' },
          {
            end: true,
            label: 'Employees & Contractors',
            to: '/payroll/employees',
          },
          { end: true, label: 'Payroll History', to: '/payroll/history' },
          {
            end: true,
            label: 'Payroll Documents',
            to: '/payroll/documents',
          },
          {
            end: true,
            label: 'Payroll Settings',
            to: '/payroll/settings',
          },
        ],
        topRoute: '/payroll',
      },
    }
  }
)
